/**
 * Date of Birth typing guard (IGFEMS-614).
 * Flatpickr matches the typed month case-sensitively and falls back to January for anything
 * it doesn't know ("25-dev-2026" and "25-dec-2026" both become 25-Jan-2026). Before the
 * widget's own handlers run, normalise typed text to dd-MMM-yyyy casing or clear it when it
 * is not a real date, so a typo is never turned into a wrong date.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const BIRTH_INPUT_SELECTOR = '.formio-component-dateOfBirth input';
const TYPED_DATE = /^(\d{1,2})-([a-z]{3})-(\d{4})(\s.*)?$/i;
// Month slot of the "99-***-9999" typing mask (letters only — the mask also allows digits).
const MONTH_SLOT_START = 3;
const MONTH_SLOT_END = 6;

/** Returns the date text with a Title-case month, or '' when it is not a real date. */
export const normalizeTypedBirthDate = (text) => {
  const match = TYPED_DATE.exec(String(text || '').trim());
  if (!match) return '';
  const day = Number(match[1]);
  const month = MONTHS.findIndex((m) => m.toLowerCase() === match[2].toLowerCase());
  const year = Number(match[3]);
  if (month < 0) return '';
  const date = new Date(year, month, day);
  if (date.getMonth() !== month || date.getDate() !== day) return '';
  return `${match[1]}-${MONTHS[month]}-${match[3]}${match[4] || ''}`;
};

const isBirthTextInput = (el) => el?.matches?.(BIRTH_INPUT_SELECTOR) && el.type !== 'hidden';

/** Attach to the form container; returns a cleanup function. */
export const attachBirthDateInputGuard = (container) => {
  if (!container) return () => {};

  const normalize = (input) => {
    if (input.value) input.value = normalizeTypedBirthDate(input.value);
  };
  const onBlur = (event) => {
    if (isBirthTextInput(event.target)) normalize(event.target);
  };
  const onKeyDown = (event) => {
    if (event.key === 'Enter' && isBirthTextInput(event.target)) normalize(event.target);
  };
  const onBeforeInput = (event) => {
    const input = event.target;
    if (!isBirthTextInput(input) || event.inputType !== 'insertText' || !/^\d$/.test(event.data || '')) return;
    if (input.selectionStart >= MONTH_SLOT_START && input.selectionStart < MONTH_SLOT_END) {
      event.preventDefault();
    }
  };

  // blur doesn't bubble — capture runs before the widget's own listeners on the input.
  container.addEventListener('blur', onBlur, true);
  container.addEventListener('keydown', onKeyDown, true);
  container.addEventListener('beforeinput', onBeforeInput, true);
  return () => {
    container.removeEventListener('blur', onBlur, true);
    container.removeEventListener('keydown', onKeyDown, true);
    container.removeEventListener('beforeinput', onBeforeInput, true);
  };
};
